/**
 * WorkerMobileNav.js - Bottom navigation bar for worker dashboard
 * Provides quick access to: Home/Jobs, My Jobs, Driver Rides (for drivers), Earnings, More (all features)
 */
import React from 'react';
import { motion } from 'framer-motion';
import { 
  Home, Briefcase, DollarSign, Search, Car, MoreHorizontal
} from 'lucide-react';

const WorkerMobileNav = ({ activeTab, onTabChange, jobCounts = {}, isDriver = false }) => {
  // Base nav items - More menu replaces Profile for access to ALL features
  const NAV_ITEMS = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'jobs', label: 'Jobs', icon: Search },
    ...(isDriver ? [{ id: 'rides', label: 'Rides', icon: Car }] : []),
    { id: 'myjobs', label: 'My Jobs', icon: Briefcase },
    { id: 'earnings', label: 'Earnings', icon: DollarSign },
    { id: 'more', label: 'More', icon: MoreHorizontal }
  ];
  
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--background-paper)] border-t border-[var(--border-primary)] safe-area-inset-bottom">
      <div className="flex items-center justify-around h-16 px-1">
        {NAV_ITEMS.map(item => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          const count = jobCounts[item.id] || 0;
          
          return (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={`relative flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors ${
                isActive 
                  ? 'text-[var(--primary)]' 
                  : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <div className="relative">
                <Icon className={`w-5 h-5 ${isActive ? 'stroke-[2.5]' : ''}`} />
                {count > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </div>
              <span className={`text-[10px] font-medium ${isActive ? 'font-semibold' : ''}`}>
                {item.label}
              </span>
              
              {/* Active indicator */}
              {isActive && (
                <motion.div
                  layoutId="navIndicator"
                  className="absolute -top-0.5 w-10 h-0.5 bg-[var(--primary)] rounded-full"
                />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default WorkerMobileNav;
